import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, Platform, Image, Alert, Animated } from 'react-native';
import { globalStyles } from '../styles/globalStyles';
import { BREAKPOINTS } from '../styles/breakpoints';
import { useUser } from '../components/LoginPersistence';
import { getFirestore, collection, getDocs, query, where, doc, getDoc, updateDoc, arrayUnion, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';
import _ from 'lodash';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import { Toast } from '../components/Toast';
import { Clipboard } from 'react-native';
// Import map embed utilities
import { extractCoordinatesFromMapEmbed } from '../utils/mapEmbedUtils';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

// Add SVG icon components at the top of the file
const MapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z"/>
    <path d="M9 3v15M15 6v15"/>
  </svg>
);

const SatelliteIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M4 10l8-4 8 4"/>
    <path d="M12 6v14M8 8v10M16 8v10"/>
    <circle cx="12" cy="20" r="2"/>
  </svg>
);

const DarkModeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/>
  </svg>
);

const ClusterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <circle cx="6" cy="6" r="2"/>
    <circle cx="18" cy="6" r="2"/>
    <circle cx="6" cy="18" r="2"/>
    <circle cx="18" cy="18" r="2"/>
  </svg>
);

const VenueIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    <circle cx="12" cy="9" r="2.5"/>
  </svg>
);

const TourIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 2l-5.5 9h11L12 2z"/>
    <path d="M17.5 11L12 20l-5.5-9"/>
  </svg>
);

const HotelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M3 21h18v-2H3v2zm2-4h14v2H5v-2zm-2-3h18v2H3v-2zm2-4h14v2H5V10zm0-5v2h14V5H5z"/>
    <path d="M10 3h4v7h-4z"/>
  </svg>
);

const GasIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M19 11h2v7c0 1.1-.9 2-2 2h-2v-7h2v-2z"/>
    <path d="M7 3h10v15c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2z"/>
    <path d="M7 8h10M7 12h10M7 16h10"/>
  </svg>
);

const ThreeDIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 3l9 4.5-9 4.5-9-4.5L12 3z"/>
    <path d="M21 7.5v9L12 21l-9-4.5v-9"/>
    <path d="M12 12v9"/>
  </svg>
);

// Add new icon components
const LocationIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
  </svg>
);

const RecenterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 8v8M8 12h8"/>
  </svg>
);

// Define styles before the component
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#121212',
    padding: 20,
    width: '100%',
    maxWidth: '100%',
    paddingTop: 80,
    height: '100vh', // Add explicit height for web
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingHorizontal: 10,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
  },
  tourNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  tourNameInput: {
    backgroundColor: '#2c2c2c',
    padding: 10,
    borderRadius: 5,
    color: '#fff',
    fontSize: 16,
    width: 200,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    position: 'relative',
    height: 'calc(100vh - 120px)', // Explicit height calculation
    [BREAKPOINTS.phone]: {
      flexDirection: 'column',
      height: 'auto',
    },
  },
  // Toggle buttons
  toggleButton: {
    position: 'absolute',
    top: '50%',
    width: 24,
    height: 60,
    backgroundColor: 'rgba(46, 46, 46, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    cursor: 'pointer',
    borderRadius: 4,
  },
  leftToggle: {
    right: -24,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  rightToggle: {
    left: -24,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  toggleButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // Venue list panel
  venueList: {
    backgroundColor: '#1e1e1e',
    borderRadius: 8,
    padding: 16,
    overflow: 'hidden', // Changed to hidden to prevent container growth
    height: 'calc(100vh - 180px)', // Fixed height that matches map container
    maxHeight: 'calc(100vh - 180px)',
    width: 300,
    position: 'relative',
    display: 'flex', // Add flex display
    flexDirection: 'column', // Ensure proper column layout
    [BREAKPOINTS.phone]: {
      maxHeight: 300,
      width: '100%',
    },
  },
  venueListScroll: {
    flex: 1,
    marginTop: 10,
    overflowY: 'auto', // For vertical scrolling
    overflowX: 'hidden', // Prevent horizontal scrolling
    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
    height: '100%', // Take full height of parent
  },
  sectionHeader: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    flexShrink: 0, // Prevent shrinking
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 8,
  },
  venueCountText: {
    color: '#aaa',
    fontSize: 14,
    marginBottom: 12,
  },
  searchInput: {
    backgroundColor: '#2c2c2c',
    padding: 12,
    borderRadius: 6,
    color: '#fff',
    fontSize: 14,
    marginTop: 8,
  },
  venueCard: {
    flexDirection: 'row',
    backgroundColor: '#2c2c2c',
    marginBottom: 12,
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'center',
    padding: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      backgroundColor: '#333333',
    },
  },
  venueImage: {
    width: 60,
    height: 60,
    borderRadius: 4,
    marginRight: 12,
  },
  venueInfo: {
    flex: 1,
    paddingRight: 12,
  },
  venueName: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 15,
    marginBottom: 6,
  },
  venueLocation: {
    color: '#aaa',
    fontSize: 13,
  },
  addButton: {
    backgroundColor: '#e67e22',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginLeft: 8,
  },
  addButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  tourVenuesList: {
    flex: 1,
    marginBottom: 10,
  },
  tourVenueCard: {
    flexDirection: 'column',
    backgroundColor: '#2c2c2c',
    marginBottom: 10,
    marginHorizontal: 8,
    borderRadius: 6,
    overflow: 'hidden',
    padding: 12,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#333333',
    },
  },
  tourVenueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  tourVenueActions: {
    flexDirection: 'column',
    gap: 8,
    marginTop: 8,
    width: '100%',
  },
  tourVenueButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    width: '100%',
  },
  removeVenueButton: {
    backgroundColor: '#ff6b6b',
    '&:hover': {
      backgroundColor: '#ff5252',
    },
  },
  viewVenueButton: {
    backgroundColor: '#2c7be5',
    '&:hover': {
      backgroundColor: '#1b69c2',
    },
  },
  tourVenueButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  dragging: {
    opacity: 0.5,
    transform: [{scale: 1.05}],
  },
  tourVenueIndex: {
    color: '#fff',
    fontWeight: 'bold',
    backgroundColor: '#e67e22',
    width: 28,
    height: 28,
    borderRadius: 14,
    textAlign: 'center',
    lineHeight: 28,
    marginRight: 12,
  },
  tourVenueInfo: {
    flex: 1,
    paddingRight: 12,
  },
  tourVenueName: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 15,
    marginBottom: 6,
  },
  tourVenueLocation: {
    color: '#aaa',
    fontSize: 13,
  },
  removeButton: {
    padding: 8,
    borderRadius: 4,
    '&:hover': {
      backgroundColor: 'rgba(255, 107, 107, 0.1)',
    },
  },
  removeButtonText: {
    color: '#ff6b6b',
    fontWeight: 'bold',
    fontSize: 16,
  },
  emptyText: {
    color: '#aaa',
    textAlign: 'center',
    marginTop: 20,
    fontStyle: 'italic',
  },
  routeInfo: {
    backgroundColor: '#2c2c2c',
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
  },
  routeInfoTitle: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
    marginBottom: 8,
  },
  routeInfoText: {
    color: '#aaa',
    fontSize: 14,
    marginBottom: 6,
  },
  saveButton: {
    backgroundColor: '#e67e22',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  disabledButton: {
    opacity: 0.5,
  },
  mobileMapText: {
    color: '#aaa',
    textAlign: 'center',
    marginTop: 20,
    padding: 20,
  },
  message: {
    color: '#aaa',
    textAlign: 'center',
    marginTop: 10,
  },
  // Add new styles for venue clustering and display controls
  mapControlsOverlay: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 10,
  },
  displayControls: {
    backgroundColor: '#1e1e1e',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
  },
  controlsTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  controlGroup: {
    marginBottom: 15,
  },
  controlLabel: {
    color: '#aaa',
    fontSize: 14,
    marginBottom: 8,
  },
  buttonGroup: {
    flexDirection: 'column',
    gap: 8,
    marginTop: 8,
    width: '100%',
  },
  displayButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 4,
    minWidth: 80,
    alignItems: 'center',
  },
  displayButtonActive: {
    backgroundColor: '#e67e22',
  },
  displayButtonText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  displayButtonTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  locationButton: {
    padding: 8,
    marginHorizontal: 2,
    borderRadius: 4,
    backgroundColor: '#2c7be5',
  },
  locationButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  locationPrompt: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    borderRadius: 8,
  },
  locationPromptText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    paddingHorizontal: 30,
  },
  locationPromptButton: {
    padding: 10,
    borderRadius: 5,
    backgroundColor: '#2c7be5',
  },
  locationPromptButtonText: {
    color: '#fff',
    fontWeight: '600',
  },
  statsOverlay: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: 8,
    borderRadius: 4,
    zIndex: 5,
  },
  statsText: {
    color: '#fff',
    fontSize: 11,
    marginBottom: 3,
  },
  proximityText: {
    fontSize: 11,
    marginTop: 2,
  },
  // Add new styles for days
  dayContainer: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    marginBottom: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'transparent',
    transition: 'all 0.2s ease',
  },
  activeDayContainer: {
    borderColor: '#e67e22',
    boxShadow: '0 0 8px rgba(230, 126, 34, 0.3)',
  },
  dayHeader: {
    backgroundColor: '#3a3a3a',
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  activeDayHeader: {
    backgroundColor: '#4a4a4a',
  },
  dayTitle: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
  activeDayTitle: {
    color: '#e67e22',
  },
  dayTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  activeDayIndicator: {
    color: '#e67e22',
    marginRight: 8,
    fontSize: 12,
  },
  editDayButton: {
    marginLeft: 8,
  },
  editIcon: {
    color: '#aaa',
    fontSize: 14,
  },
  saveIcon: {
    color: '#4caf50',
    fontSize: 18,
    marginLeft: 8,
  },
  dayNameInput: {
    backgroundColor: '#2c2c2c',
    padding: 6,
    borderRadius: 4,
    color: '#fff',
    flex: 1,
  },
  dayEditContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  removeDayButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeDayButtonText: {
    color: '#ff6b6b',
    fontSize: 18,
    fontWeight: 'bold',
  },
  addDayButton: {
    backgroundColor: '#2c2c2c',
    padding: 10,
    borderRadius: 6,
    marginTop: 12,
    alignItems: 'center',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#333333',
    },
  },
  addDayButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  emptyDayContainer: {
    padding: 20,
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: '#555',
    margin: 12,
    borderRadius: 6,
    minHeight: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyDayText: {
    color: '#888',
    fontStyle: 'italic',
    textAlign: 'center',
    fontSize: 14,
  },
  tourHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  activeEmptyDayContainer: {
    borderColor: '#e67e22',
    borderStyle: 'dashed',
  },
  activeVenueCard: {
    backgroundColor: '#333333',
  },
  activeTourVenueIndex: {
    backgroundColor: '#ff7e29',
  },
  // Add new styles for coming soon message
  comingSoonContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#121212',
  },
  comingSoonTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 10,
  },
  comingSoonSubtitle: {
    fontSize: 24,
    color: '#e67e22',
    marginBottom: 20,
  },
  comingSoonMessage: {
    fontSize: 16,
    color: '#aaa',
    textAlign: 'center',
    marginBottom: 30,
    maxWidth: 400,
    lineHeight: 24,
  },
  backButton: {
    backgroundColor: '#e67e22',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
  },
  backButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  bottomControls: {
    position: 'absolute',
    bottom: 20,
    right: 20, // Changed from left to right
    flexDirection: 'row',
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: 12,
    padding: 8, // Reduced padding
    gap: 16, // Reduced gap
    zIndex: 10,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  controlIconGroup: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 12,
    borderRightWidth: 1,
    borderRightColor: 'rgba(255, 255, 255, 0.1)',
    '&:last-child': {
      borderRightWidth: 0,
    },
  },
  controlIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 6, // Reduced padding
    borderRadius: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    minWidth: 48, // Reduced width
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  activeIcon: {
    backgroundColor: '#e67e22',
    '&:hover': {
      backgroundColor: '#f39c12',
    },
  },
  iconLabel: {
    color: '#fff',
    fontSize: 10, // Reduced font size
    fontWeight: '500',
    marginTop: 4,
    textAlign: 'center',
  },
  clusterSection: {
    padding: 10,
    backgroundColor: 'rgba(230, 126, 34, 0.1)',
    borderRadius: 5,
    marginBottom: 10,
  },
  clusterTitle: {
    color: '#e67e22',
    fontSize: 14,
    fontWeight: '600',
  },
  filtersContainer: {
    marginTop: 12,
    marginBottom: 16,
    backgroundColor: '#2c2c2c',
    borderRadius: 8,
    padding: 12,
  },
  filterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  filterGroup: {
    flex: 1,
    marginHorizontal: 4,
  },
  filterLabel: {
    color: '#aaa',
    fontSize: 12,
    marginBottom: 4,
  },
  filterButton: {
    backgroundColor: '#3a3a3a',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
  },
  filterButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  distanceText: {
    color: '#e67e22',
    fontSize: 12,
    marginTop: 2,
  },
  venueDetails: {
    marginTop: 4,
  },
  detailText: {
    color: '#aaa',
    fontSize: 12,
  },
  recenterButton: {
    position: 'absolute',
    top: 60,
    right: 20,
    zIndex: 1,
    backgroundColor: 'rgba(46, 46, 46, 0.9)',
    width: 32,
    height: 32,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(46, 46, 46, 1)',
    },
  },
  recenterButtonText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '500',
  },
  bottomLeftControls: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    zIndex: 10,
  },
  locationButtonContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
  },
  locationButton: {
    backgroundColor: 'rgba(46, 46, 46, 0.9)',
    width: 32,
    height: 32,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(46, 46, 46, 1)',
    },
  },
  locationButtonIcon: {
    color: '#fff',
    fontSize: 20,
  },
  mapContainer: {
    flex: 1,
    minHeight: '500px', // Add minimum height
    position: 'relative',
    [BREAKPOINTS.phone]: {
      height: '400px',
      minHeight: '400px',
    },
  },
  mileageText: {
    color: '#2c7be5', // Light blue color
    fontSize: 13,
    fontWeight: '500',
  },
  dayRouteInfo: {
    backgroundColor: 'rgba(44, 123, 229, 0.1)', // Light blue with opacity
    padding: 8,
    borderRadius: 4,
    marginTop: 8,
    marginBottom: 8,
  },
  dayRouteLeg: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  dayRouteText: {
    color: '#fff',
    fontSize: 12,
  },
  dayRouteMileage: {
    color: '#2c7be5',
    fontSize: 12,
    fontWeight: '500',
  },
  totalMileage: {
    color: '#2c7be5',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: 'rgba(44, 123, 229, 0.2)',
    paddingTop: 4,
  },
  fuelEstimateText: {
    color: '#2c7be5',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
    marginTop: 6,
  },
  buttonIcon: {
    color: '#fff',
    width: 16,
    height: 16,
  },
  bottomRightControls: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  controlButton: {
    backgroundColor: 'rgba(46, 46, 46, 0.9)',
    width: 32,
    height: 32,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(46, 46, 46, 1)',
    },
  },
  buttonIcon: {
    width: 16,
    height: 16,
    color: '#fff',
  },
  filterContainer: {
    padding: 10,
    backgroundColor: '#2c2c2c',
    borderRadius: 8,
    margin: 10,
  },
  filterTitle: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 10,
  },
  filterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterButton: {
    backgroundColor: '#444',
    padding: 8,
    borderRadius: 4,
    minWidth: 80,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#2c7be5',
  },
  filterButtonText: {
    color: '#fff',
    fontSize: 14,
  },
  venueFeatures: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 5,
  },
  venueFeature: {
    color: '#2c7be5',
    fontSize: 12,
  },
  tourVenueButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  viewDetailsButton: {
    backgroundColor: '#2c7be5',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    width: '100%',
    '&:hover': {
      backgroundColor: '#1b69c2',
    },
  },
  viewDetailsButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Update button styles for consistency
  actionButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    width: '100%',
    marginBottom: 8,
  },
  primaryButton: {
    backgroundColor: '#e67e22',
    '&:hover': {
      backgroundColor: '#d35400',
    },
  },
  secondaryButton: {
    backgroundColor: '#2c7be5',
    '&:hover': {
      backgroundColor: '#1b69c2',
    },
  },
  dangerButton: {
    backgroundColor: '#ff6b6b',
    '&:hover': {
      backgroundColor: '#ff5252',
    },
  },
  buttonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modeControls: {
    position: 'absolute',
    top: 20,
    left: 20,
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: 8,
    padding: 8,
    zIndex: 10,
    flexDirection: 'row',
    gap: 8,
  },
  modeButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    minWidth: 80,
    alignItems: 'center',
  },
  modeButtonActive: {
    backgroundColor: '#e67e22',
  },
  modeButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  modeButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  drawInstructions: {
    position: 'absolute',
    top: 80,
    left: 20,
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    padding: 12,
    borderRadius: 8,
    zIndex: 10,
    maxWidth: 300,
  },
  drawInstructionsText: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
  },
  drawModeContainer: {
    position: 'absolute',
    top: 80,
    left: 20,
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    padding: 12,
    borderRadius: 8,
    zIndex: 10,
    maxWidth: 300,
  },
  drawModeInstructions: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  drawButton: {
    backgroundColor: '#e67e22',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    marginBottom: 8,
  },
  drawButtonActive: {
    backgroundColor: '#d35400',
  },
  drawButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  clearButton: {
    backgroundColor: '#ff6b6b',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
  },
  clearButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  tourControls: {
    flexDirection: 'row',
    gap: 10,
    marginLeft: 20,
  },
  newTourButton: {
    backgroundColor: '#2c7be5',
    padding: 8,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  saveButton: {
    backgroundColor: '#4caf50',
    padding: 8,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  newTourButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  loadButton: {
    backgroundColor: '#2196f3',
    padding: 8,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  loadButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  shareButton: {
    backgroundColor: '#9c27b0',
    padding: 8,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  shareButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  selectedVenueCard: {
    backgroundColor: 'rgba(230, 126, 34, 0.3)',
    borderColor: '#e67e22',
    borderWidth: 1,
  },
  tourVenueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  '.dark-popup .mapboxgl-popup-content': {
    backgroundColor: '#2c2c2c',
    color: '#ffffff',
    padding: '15px',
    borderRadius: '8px',
    minWidth: '200px',
  },
  '.dark-popup .mapboxgl-popup-close-button': {
    color: '#ffffff',
    fontSize: '16px',
    padding: '5px',
    right: '5px',
    top: '5px',
  },
  '.dark-popup .mapboxgl-popup-close-button:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
  },
});

// Add helper functions before the component
const isValidLatitude = (lat) => {
  const parsedLat = parseFloat(lat);
  return !isNaN(parsedLat) && parsedLat >= -90 && parsedLat <= 90;
};

const isValidLongitude = (lng) => {
  const parsedLng = parseFloat(lng);
  return !isNaN(parsedLng) && parsedLng >= -180 && parsedLng <= 180;
};

// Helper function to convert lat/lng to latitude/longitude format
const convertCoordinateFormat = (coords) => {
  if (!coords || coords.lat === null || coords.lng === null) return null;
      return {
    latitude: coords.lat,
    longitude: coords.lng
  };
};

// Add new function for getting directions between points
const getDirections = async (start, end) => {
  try {
    console.log('Calculating route...');
    let startCoords, endCoords;
    
    // Handle both array and object formats
    if (Array.isArray(start)) {
      startCoords = start;
    } else {
      startCoords = [start.lng, start.lat];
    }
    
    if (Array.isArray(end)) {
      endCoords = end;
    } else {
      endCoords = [end.lng, end.lat];
    }
    
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${startCoords[0]},${startCoords[1]};${endCoords[0]},${endCoords[1]}?geometries=geojson&access_token=${MAPBOX_ACCESS_TOKEN}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      return {
        distance: route.distance / 1000, // Convert to kilometers
        duration: route.duration / 60, // Convert to minutes
        geometry: route.geometry
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting directions:', error);
    return null;
  }
};

export function TourPlanner({ navigation, route }) {
  const { user: userData } = useUser();
  const [venues, setVenues] = useState([]);
  const [displayedVenues, setDisplayedVenues] = useState([]);
  const [tourVenues, setTourVenues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [tourName, setTourName] = useState('My Tour');
  const [isDragging, setIsDragging] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [clusterVenues, setClusterVenues] = useState([]);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null); // Reference for MapboxDraw instance
  const routeCache = useRef({}); // Cache for route data to avoid redundant API calls
  const auth = getAuth();
  
  // Add POI filter states
  const [showHotels, setShowHotels] = useState(false);
  const [showGasStations, setShowGasStations] = useState(false);
  
  // Add display options state
  const [displayMode, setDisplayMode] = useState('clusters'); // 'clusters', 'all', 'tour'
  const [clusterRadius, setClusterRadius] = useState(50);
  const [mapBounds, setMapBounds] = useState(null);
  
  // Location & zoom tracking state
  const [userLocation, setUserLocation] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(3);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  
  // Panel visibility states
  const [venueListVisible, setVenueListVisible] = useState(true);
  const [tourStopsVisible, setTourStopsVisible] = useState(true);
  
  // Animation values for panel sliding
  const venueListAnim = useRef(new Animated.Value(1)).current;
  const tourStopsAnim = useRef(new Animated.Value(1)).current;

  // Add state for days organization
  const [tourDays, setTourDays] = useState([{ 
    id: 'day-1', 
    name: 'Day 1', 
    venues: [] 
  }]);
  const [draggedVenue, setDraggedVenue] = useState(null);
  const [editingDayId, setEditingDayId] = useState(null);
  const [newDayName, setNewDayName] = useState('');
  
  // Add state for active day
  const [activeDayId, setActiveDayId] = useState('day-1');

  // Check if a venue was passed via route params to be added to the tour
  const venueFromRoute = route.params?.addVenue;

  // Check user permissions
  const isAdmin = userData && userData.userType === 'admin';
  const canUseTourPlanner = isAdmin;

  // Add new state for map style
  const [mapStyle, setMapStyle] = useState('streets-v11');

  // Add new state for hover info
  const [hoverInfo, setHoverInfo] = useState(null);

  // Add filter states
  const [filters, setFilters] = useState({
    capacity: 'all',
    venueType: 'all',
    state: 'all',
    hasParking: 'all'
  });

  // Add state for initial map center
  const [initialCenter, setInitialCenter] = useState([-98.5795, 39.8283]); // Center of US

  // Add new state variables at the top of the component
  const [hotels, setHotels] = useState([]);
  const [gasStations, setGasStations] = useState([]);
  const [loadingPOIs, setLoadingPOIs] = useState(false);

  // Add new state for map modes
  const [mapMode, setMapMode] = useState('travel'); // 'travel', 'draw', 'data'
  const [drawBoundary, setDrawBoundary] = useState(null);

  // Helper function to convert degrees to radians
  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };

  // Function to calculate distance between two points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const d = R * c; // Distance in km
    return d * 0.621371; // Convert to miles
  };

  // Enhance coordinate validation with more strict checks
  const isValidCoordinate = (venue) => {
    if (!venue.latitude || !venue.longitude) return false;
    
    const lat = parseFloat(venue.latitude);
    const lng = parseFloat(venue.longitude);
    
    // Basic range checks
    if (!isValidLatitude(lat) || !isValidLongitude(lng)) return false;
    
    // Check for common invalid coordinates
    if (lat === 0 && lng === 0) return false; // Null Island
    if (Math.abs(lat) < 0.1 && Math.abs(lng) < 0.1) return false; // Too close to 0,0
    
    // Check if coordinates are in the ocean (basic check)
    // This is a simple check that can be enhanced with more sophisticated ocean detection
    const isLikelyOcean = [
      { lat: 0, lng: 0, radius: 10 }, // Null Island
      { lat: -48.876667, lng: -123.393333, radius: 20 }, // Point Nemo
      // Add more known ocean points if needed
    ].some(point => {
      const distance = calculateDistance(lat, lng, point.lat, point.lng);
      return distance < point.radius;
    });
    
    if (isLikelyOcean) return false;
    
    // For US venues, ensure they're roughly within continental US bounds
    if (venue.country === 'US' || venue.country === 'USA') {
      return lat >= 24.396308 && lat <= 49.384358 && // Latitude bounds for continental US
             lng >= -125.000000 && lng <= -66.934570; // Longitude bounds for continental US
    }
    
    return true;
  };

  // Toggle panel visibility with animation
  const toggleVenueList = () => {
    if (venueListVisible) {
      Animated.timing(venueListAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setVenueListVisible(false));
    } else {
      setVenueListVisible(true);
      Animated.timing(venueListAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const toggleTourStops = () => {
    if (tourStopsVisible) {
      Animated.timing(tourStopsAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setTourStopsVisible(false));
    } else {
      setTourStopsVisible(true);
      Animated.timing(tourStopsAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

    // Function to sort venues by distance from user location
  const sortVenuesByDistance = () => {
    if (!userLocation || !venues || venues.length === 0) return;
    
    console.log('Sorting venues by distance from user location...');
    
    // Calculate and store distances to avoid recalculating during sort
    const venuesWithDistance = venues.map(venue => {
      if (!venue.latitude || !venue.longitude) {
        return { ...venue, distance: Infinity };
      }
      
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        venue.latitude,
        venue.longitude
      );
      
      return { ...venue, distance };
    });
    
    // Sort by distance and take only the top 100
    const sortedVenues = venuesWithDistance
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 100)
      .map(v => {
        // Remove the distance property before setting state
        const { distance, ...venueWithoutDistance } = v;
        return venueWithoutDistance;
      });

    console.log(`Showing ${sortedVenues.length} closest venues`);
    setDisplayedVenues(sortedVenues);
  };

  // Request user's geolocation
  const requestUserLocation = () => {
    if (navigator && navigator.geolocation) {
      setLocationRequested(true);
      
      try {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ latitude, longitude });
            
            // Add user location marker to map
            if (map.current) {
              // Remove existing user location marker if it exists
              const existingMarker = document.querySelector('.user-location-marker');
              if (existingMarker) {
                existingMarker.remove();
              }

              // Create and add new marker
              const marker = new mapboxgl.Marker({
                color: '#2c7be5',
                scale: 0.8,
                className: 'user-location-marker'
              })
                .setLngLat([longitude, latitude])
                .addTo(map.current);

              // Center map on user location
              map.current.flyTo({
                center: [longitude, latitude],
                zoom: 10,
                essential: true
              });
            }

            // Sort venues by distance from user location
            if (venues && venues.length > 0) {
              // Calculate and store distances to avoid recalculating during sort
              const venuesWithDistance = venues.map(venue => {
                if (!venue.latitude || !venue.longitude) {
                  return { ...venue, distance: Infinity };
                }
                
                const distance = calculateDistance(
                latitude,
                longitude,
                  venue.latitude,
                  venue.longitude
              );
              
                return { ...venue, distance };
              });
              
              // Sort by distance and take only the top 100
              const sortedVenues = venuesWithDistance
                .sort((a, b) => a.distance - b.distance)
                .slice(0, 100)
                .map(v => {
                  // Remove the distance property before setting state
                  const { distance, ...venueWithoutDistance } = v;
                  return venueWithoutDistance;
            });

              console.log(`Showing ${sortedVenues.length} closest venues`);
            setDisplayedVenues(sortedVenues);
            }
          },
          (error) => {
            console.error('Error getting location:', error);
            setLocationPermissionDenied(true);
            // Even if we can't get the location, still show venues
            setDisplayedVenues(venues.slice(0, 100));
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
        );
      } catch (error) {
        console.error('Geolocation error:', error);
        setLocationPermissionDenied(true);
        // Even if there's an error, still display venues
        setDisplayedVenues(venues.slice(0, 100));
      }
    } else {
      // No geolocation support, still show venues
      setDisplayedVenues(venues.slice(0, 100));
      setLocationPermissionDenied(true);
    }
  };

  // Update fetchVenues function
  const fetchVenues = async () => {
    try {
      console.log('Fetching venues...');
      const db = getFirestore();
      const venuesRef = collection(db, 'venues');
      const venuesSnapshot = await getDocs(venuesRef);
      
      console.log(`Retrieved ${venuesSnapshot.docs.length} venues`);
      
      const venuesData = venuesSnapshot.docs.map(doc => {
        const data = doc.data();
        let coordinates = null;
        
        // First try to use existing coordinates
        if (data.latitude && data.longitude) {
          coordinates = {
            latitude: parseFloat(data.latitude),
            longitude: parseFloat(data.longitude)
          };
        }
        // If no coordinates, try to extract from map embed
        else if (data.mapEmbedLink) {
          const coords = extractCoordinatesFromMapEmbed(data.mapEmbedLink);
          if (coords.lat !== null && coords.lng !== null) {
            coordinates = convertCoordinateFormat(coords);
          }
        }
        
        const venue = {
          id: doc.id,
          ...data,
          ...(coordinates || {}) // Spread coordinates if they exist
        };
        
        return venue;
      });

      // Filter out venues without valid coordinates
      const validVenues = venuesData.filter(venue => {
        const isValid = isValidLatitude(venue.latitude) && isValidLongitude(venue.longitude);
        if (!isValid) {
          console.log(`Invalid coordinates for venue: ${venue.name}`);
        }
        return isValid;
      });

      console.log(`Valid venues processed: ${validVenues.length}`);
      
      setVenues(validVenues);
      
      // If we have user location, sort venues by distance
      if (userLocation) {
        console.log(`User location is available, sorting venues by distance`);
        // Sort venues by their distance from user
        const venuesWithDistance = validVenues.map(venue => {
          if (!venue.latitude || !venue.longitude) {
            return { ...venue, distance: Infinity };
          }
          
          const distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            venue.latitude,
            venue.longitude
          );
          
          return { ...venue, distance };
        });
        
        // Sort by distance and take only the top 100
        const sortedVenues = venuesWithDistance
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 100)
          .map(v => {
            // Remove the distance property before setting state
            const { distance, ...venueWithoutDistance } = v;
            return venueWithoutDistance;
          });

        console.log(`Showing ${sortedVenues.length} closest venues based on user location`);
        setDisplayedVenues(sortedVenues);
      } else {
        // If no user location, just show the first 100 venues
        console.log(`No user location yet, showing first 100 venues`);
        setDisplayedVenues(validVenues.slice(0, 100));
        
        // Try to get user location if not already requested
        if (!locationRequested) {
          console.log(`Requesting user location to improve venue sorting`);
          requestUserLocation();
        }
      }
      
      // Update the map with the valid venues
      if (map.current && mapLoaded) {
        console.log('Updating map display...');
        updateVenuesOnMap(validVenues);
      } else {
        console.log('Map not ready yet, venues will be added when map loads');
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
      Alert.alert('Error', 'Failed to load venues. Please try again.');
    }
  };

  // Add recenter map function
  const recenterMap = () => {
    if (!map.current) return;

    if (venues.length > 0) {
      // If we have venues, fit to all venues
      const bounds = new mapboxgl.LngLatBounds();
      venues.forEach(venue => {
        if (venue.latitude && venue.longitude) {
          bounds.extend([venue.longitude, venue.latitude]);
        }
      });
      
      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 12
        });
        return;
      }
    }
    
    // If no venues or bounds are empty, go to default center
    map.current.flyTo({
      center: initialCenter,
      zoom: 3,
      essential: true
    });
  };

  useEffect(() => {
    // Redirect if user doesn't have permissions
    if (userData && !canUseTourPlanner) {
      Alert.alert(
        "Access Restricted",
        "You must be an Admin, Artist, or Touring Crew member to use the Tour Planner.",
        [{ text: "Go Back", onPress: () => navigation.goBack() }]
      );
      return;
    }

    // Fetch venues
    fetchVenues();
  }, [userData, navigation]);

  // Add the venue from route params to tour if provided
  useEffect(() => {
    if (venueFromRoute && venueFromRoute.id) {
      const alreadyInTour = tourVenues.some(venue => venue.id === venueFromRoute.id);
      if (!alreadyInTour) {
        setTourVenues(prev => [...prev, venueFromRoute]);
        // Add venue to the active day
        addVenueToDayById(venueFromRoute, activeDayId);
      }
    }
  }, [venueFromRoute]);
  
  // Add this effect to update tour stats whenever tour venues or tour days change
  useEffect(() => {
    if (mapLoaded && map.current) {
      console.log('Tour data changed, updating map and routes');
      
      // Force display mode to tour if we have venues in the tour
      const allTourVenues = tourDays.flatMap(day => day.venues);
      const hasTourVenues = allTourVenues.length > 0;
      
      if (hasTourVenues && displayMode !== 'tour') {
        console.log('Switching to tour display mode to show routes');
        setDisplayMode('tour');
      }
      
      // Update venues on map with priority for tour venues
      if (displayMode === 'tour' || hasTourVenues) {
        console.log('Updating map with tour venues');
        updateVenuesOnMap(allTourVenues);
      } else {
        updateVenuesOnMap(venues);
      }
      
      // Calculate and update route information if we have multiple venues
      const activeDay = tourDays.find(day => day.id === activeDayId);
      const activeDayHasMultipleVenues = activeDay && activeDay.venues.length >= 2;
      
      if (activeDayHasMultipleVenues || allTourVenues.length >= 2) {
        console.log('Calculating tour routes');
        calculateTourDistances().then(info => {
          if (info) {
            setRouteInfo(info);
            console.log('Updated tour info:', info);
          }
        });
      }
    }
  }, [tourVenues, tourDays, mapLoaded, activeDayId, displayMode]);

  useEffect(() => {
    // Initialize map only on web platform and when the map isn't already initialized
    if (Platform.OS === 'web' && mapContainer.current && !map.current) {
      console.log('Initializing map...');
      
      if (!MAPBOX_ACCESS_TOKEN) {
        console.error('Map configuration error');
        return;
      }
      
      try {
        // Make sure mapboxgl is properly initialized
        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
        
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: initialCenter,
          zoom: 3,
          maxZoom: 22,
          trackResize: true,
          maxPitch: 85,
          renderWorldCopies: true,
          localIdeographFontFamily: "'Noto Sans', 'Noto Sans CJK SC', sans-serif",
          preserveDrawingBuffer: true,
          customAttribution: 'VRC Tour Planner'
        });

        // Set up event handlers
        map.current.on('load', () => {
          console.log('Map loaded, initializing components...');
          setMapLoaded(true);

          // Add source for venues
          if (!map.current.getSource('venues-source')) {
            map.current.addSource('venues-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: []
              },
              cluster: true,
              clusterMaxZoom: 14,
              clusterRadius: 50,
              clusterProperties: {
                sum: ['+', ['get', 'capacity']]
              }
            });
          }

          // Add layers for venues if they don't exist
          if (!map.current.getLayer('clusters')) {
            map.current.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  20, 100,
                  30, 750,
                  40
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff'
              }
            });
          }

          if (!map.current.getLayer('cluster-count')) {
            map.current.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
              },
              paint: {
                'text-color': '#ffffff'
              }
            });
          }

          // Add the unclustered points layer
          if (!map.current.getLayer('unclustered-point')) {
            map.current.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                // Differentiate tour venues from other venues
                'circle-color': [
                  'case',
                  ['==', ['get', 'isTourVenue'], true], 
                  '#4a90e2', // Blue for tour venues
                  '#e67e22'  // Orange for regular venues
                ],
                'circle-radius': [
                  'case',
                  ['==', ['get', 'isTourVenue'], true], 
                  10,  // Slightly larger for tour venues
                  8    // Regular size for other venues
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
              }
            });
          }

          // Add the venue labels layer
          if (!map.current.getLayer('venue-labels')) {
            map.current.addLayer({
              id: 'venue-labels',
              type: 'symbol',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              layout: {
                'text-field': ['get', 'name'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'text-offset': [0, 1.5],
                'text-anchor': 'top'
              },
              paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#000000',
                'text-halo-width': 2
              }
            });
          }

          // Update venues on map after initialization
          if (venues.length > 0) {
            updateVenuesOnMap(venues);
          }

          // Set up click handlers
          setupVenueClickHandlers();
        });

        // Add navigation controls
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

        // Handle map movement
        map.current.on('moveend', () => {
          const bounds = map.current.getBounds();
          setMapBounds(bounds);
        });

        // Handle zoom changes
        map.current.on('zoom', () => {
          setCurrentZoom(map.current.getZoom());
        });

      } catch (error) {
        console.error('Error initializing map:', error);
        Alert.alert('Error', 'Failed to initialize map. Please try again.');
      }
    }
  }, [mapContainer.current, MAPBOX_ACCESS_TOKEN]);

  // Separate useEffect for handling permissions and data loading
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch venues regardless of permissions
        // The fetchVenues function will also handle requesting user location if needed
        await fetchVenues();
      } catch (error) {
        console.error('Error initializing data:', error);
        // Even on error, make sure venues are displayed
        if (venues.length > 0) {
          setDisplayedVenues(venues.slice(0, 100));
        }
      }
    };

    initializeData();
  }, []);
  
  // Add effect to sort venues when user location becomes available
  useEffect(() => {
    if (userLocation && venues.length > 0) {
      console.log('User location changed, resorting venues by distance');
      sortVenuesByDistance();
    }
  }, [userLocation, venues.length]);

  // Create a dedicated function for setting up click handlers
  const setupVenueClickHandlers = () => {
    if (!map.current) return;

    // Click handler for individual venues
    map.current.on('click', 'unclustered-point', (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;
      const venue = venues.find(v => v.id === properties.id);

      if (!venue) {
        console.error('Venue not found:', properties.id);
        return;
      }

      // Ensure proper zoom level
      map.current.flyTo({
        center: coordinates,
        zoom: Math.max(map.current.getZoom(), 15),
        essential: true
      });

      // Show popup with venue details
      const popupContent = `
        <div style="text-align: center;">
          <h3 style="margin: 0 0 10px 0; color: #ffffff;">${venue.name}</h3>
          <p style="margin: 0 0 15px 0; color: #cccccc;">${venue.city}, ${venue.state}</p>
          <div style="display: flex; gap: 10px; justify-content: center;">
            <button 
              onclick="window.handleMapPopupAction('addToTour', '${venue.id}')"
              style="
                background: #4a90e2;
                color: white;
                border: none;
                padding: 8px 16px;
                border-radius: 4px;
                cursor: pointer;
                font-weight: 500;
                transition: background 0.2s;
              "
              onmouseover="this.style.background='#357abd'"
              onmouseout="this.style.background='#4a90e2'"
            >
              Add to Tour
            </button>
            <button
              onclick="window.handleMapPopupAction('viewDetails', '${venue.id}')"
              style="
                background: #2c2c2c;
                color: white;
                border: 1px solid #4a90e2;
                padding: 8px 16px;
                border-radius: 4px;
                cursor: pointer;
                font-weight: 500;
                transition: all 0.2s;
              "
              onmouseover="this.style.background='#4a90e2'"
              onmouseout="this.style.background='#2c2c2c'"
            >
              View Details
            </button>
          </div>
        </div>
      `;

      new mapboxgl.Popup({ className: 'dark-popup' })
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(map.current);
    });

    // Click handler for clusters
    map.current.on('click', 'clusters', (e) => {
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });
      const clusterId = features[0].properties.cluster_id;
      
      map.current.getSource('venues-source').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;

          map.current.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          });
        }
      );
    });

    // Change cursor on hover
    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = '';
    });

    map.current.on('mouseenter', 'clusters', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = '';
    });
  };

  // Define addVenueToTour before it's used in useEffect
  const addVenueToTour = (venue) => {
    if (!venue.latitude || !venue.longitude) {
      Alert.alert('Cannot Add Venue', 'This venue does not have location coordinates.');
      return;
    }

    // Check if venue already exists in tour
    if (tourVenues.some(v => v.id === venue.id)) {
      Alert.alert('Venue Already Added', 'This venue is already in your tour.');
      return;
    }

    // *** CRITICAL FIX for route drawing ***
    // ALWAYS force display mode to "tour" when adding a venue
    if (displayMode !== 'tour') {
      console.log('Forcing tour display mode for route visibility');
      setDisplayMode('tour');
    }

    // Create a new array with all tour venues plus the new one
    const updatedTourVenues = [...tourVenues, venue];
    
    // Update tour venues state
    setTourVenues(updatedTourVenues);
    
    // Find the active day to check if it will have multiple venues after adding this one
    const activeDay = tourDays.find(d => d.id === activeDayId);
    const willHaveMultipleVenues = activeDay && activeDay.venues.length >= 1;
    
    // First update the tour days - use a callback to get the latest state
    setTourDays(prevDays => {
      // Create new tour days array with the venue added
      const newTourDays = prevDays.map(day => 
        day.id === activeDayId 
          ? { ...day, venues: [...day.venues, venue] }
          : day
      );
      
      // Schedule an immediate route update after state changes
      if (willHaveMultipleVenues && map.current && mapLoaded) {
        // Add a minimal delay to allow state to update first
        setTimeout(() => {
          console.log('Forcing IMMEDIATE route drawing after venue addition');
          
          // Get the updated day with the new venue
          const updatedActiveDay = newTourDays.find(d => d.id === activeDayId);
          if (!updatedActiveDay) return;
          
          // Get all venues including the newly added one
          const allVenues = newTourDays.flatMap(day => day.venues);
          
          // Direct call to update venues on map with non-debounced function
          directUpdateMapWithRoutes(allVenues, newTourDays, activeDayId);
          
          // Force another update 500ms later to ensure routes stay visible
          setTimeout(() => {
            console.log('Follow-up route drawing to ensure visibility');
            directUpdateMapWithRoutes(allVenues, newTourDays, activeDayId);
            
            // Add a third update with a longer delay to make sure everything stays visible
            setTimeout(() => {
              console.log('Final route verification to ensure continued visibility');
              directUpdateMapWithRoutes(allVenues, newTourDays, activeDayId);
              
              // Force display mode to 'tour' again to ensure proper visibility
              if (displayMode !== 'tour') {
                console.log('Forcing tour display mode for long-term route visibility');
                setDisplayMode('tour');
              }
            }, 1000);
          }, 500);
        }, 50);
      }
      
      return newTourDays;
    });
  };

  // New direct update function that bypasses debouncing for immediate route drawing
  const directUpdateMapWithRoutes = async (allVenues, currentTourDays, currentActiveDayId) => {
    if (!map.current || !mapLoaded) return;
    
    console.log('DIRECT route update running - bypassing debounce');
    
    try {
      // Clear existing routes ONLY, not the venue source data
      const existingLayers = map.current.getStyle().layers;
      existingLayers.forEach(layer => {
        if (layer.id.startsWith('route-') && map.current.getLayer(layer.id)) {
          map.current.removeLayer(layer.id);
        }
      });
      
      const existingSources = Object.keys(map.current.getStyle().sources);
      existingSources.forEach(source => {
        if (source.startsWith('route-') && map.current.getSource(source)) {
          map.current.removeSource(source);
        }
      });

      // Instead of updating venues source directly with just tour venues,
      // we need to preserve all venues while highlighting tour venues
      // Create a new array that includes ALL displayed venues
      const allMapVenues = [...displayedVenues];
      
      // Make sure we don't lose the newly added venue
      const updatedTourVenues = allVenues.filter(venue => 
        venue.id && venue.latitude && venue.longitude
      );
      
      // Only update the venues that are part of the tour - don't replace all venues
      if (map.current.getSource('venues-source')) {
        // Create features from all venues but with special properties for tour venues
        const features = allMapVenues.map(venue => {
          // Check if this venue is in our tour
          const isTourVenue = updatedTourVenues.some(tv => tv.id === venue.id);
          
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [parseFloat(venue.longitude), parseFloat(venue.latitude)]
            },
            properties: {
              id: venue.id,
              name: venue.name,
              city: venue.city,
              state: venue.state,
              capacity: venue.capacity,
              affiliate: venue.affiliate,
              isTourVenue: isTourVenue // Add property to identify tour venues
            }
          };
        });

        // Update the venue source with all venues
        map.current.getSource('venues-source').setData({
          type: 'FeatureCollection',
          features: features.filter(f => 
            f.geometry.coordinates[0] && 
            f.geometry.coordinates[1] && 
            !isNaN(f.geometry.coordinates[0]) && 
            !isNaN(f.geometry.coordinates[1])
          )
        });
      }

      // Get the active day
      const activeDay = currentTourDays.find(d => d.id === currentActiveDayId);
      if (!activeDay || activeDay.venues.length < 2) return;
      
      const dayIndex = currentTourDays.findIndex(d => d.id === currentActiveDayId);
      console.log(`Drawing routes for day ${activeDay.name} with ${activeDay.venues.length} venues`);
      
      // Draw routes between each consecutive pair of venues
      for (let i = 0; i < activeDay.venues.length - 1; i++) {
        const start = activeDay.venues[i];
        const end = activeDay.venues[i + 1];
        
        if (!start || !end || 
            !start.longitude || !start.latitude || 
            !end.longitude || !end.latitude) {
          continue;
        }
        
        // Get route data
        const cacheKey = `${start.id}-${end.id}`;
        let routeData = routeCache.current[cacheKey];
        
        if (!routeData) {
          console.log(`Getting NEW route from ${start.name} to ${end.name}`);
          routeData = await getDirections(
            [parseFloat(start.longitude), parseFloat(start.latitude)],
            [parseFloat(end.longitude), parseFloat(end.latitude)]
          );
          
          if (routeData && routeData.geometry) {
            routeCache.current[cacheKey] = routeData;
          }
        } else {
          console.log(`Using CACHED route from ${start.name} to ${end.name}`);
        }
        
        if (routeData && routeData.geometry) {
          const sourceId = `route-${dayIndex}-${i}`;
          const layerId = `route-layer-${dayIndex}-${i}`;

          // Add source
          if (!map.current.getSource(sourceId)) {
            map.current.addSource(sourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: routeData.geometry
              }
            });
          } else {
            map.current.getSource(sourceId).setData({
              type: 'Feature',
              geometry: routeData.geometry
            });
          }

          // Add layer
          if (!map.current.getLayer(layerId)) {
            map.current.addLayer({
              id: layerId,
              type: 'line',
              source: sourceId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': ROUTE_COLORS[dayIndex % ROUTE_COLORS.length],
                'line-width': 3,
                'line-opacity': 0.8
              }
            });
          }
        }
      }
    } catch (error) {
      console.error('Error in direct route update:', error);
    }
  };

  // Add function to handle adding venue from map popup
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Define the handler for popup buttons
      window.handleMapPopupAction = (action, venueId) => {
        const venue = venues.find(v => v.id === venueId);
        if (!venue) return;
        
        switch (action) {
          case 'addToTour':
            addVenueToTour(venue);
            break;
          case 'viewDetails':
            handleVenueSelect(venue);
            break;
        }
      };
      
      // Keep backwards compatibility
      window.addVenueToTourFromMap = (venueId) => {
        const venue = venues.find(v => v.id === venueId);
        if (venue) {
          addVenueToTour(venue);
        }
      };
    }
    
    // Clean up function when component unmounts
    return () => {
      if (typeof window !== 'undefined') {
        window.handleMapPopupAction = null;
        window.addVenueToTourFromMap = null;
      }
    };
  }, [venues]);

  // Toggle display mode between clusters, all venues, and tour venues
  const toggleDisplayMode = (mode) => {
    if (!map.current) return;
    
    switch (mode) {
      case 'satellite':
        const currentStyle = map.current.getStyle().name;
        const newStyle = currentStyle.includes('Satellite') ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/satellite-streets-v12';
        map.current.setStyle(newStyle);
        // Re-add sources and layers after style change
        map.current.once('style.load', () => {
          // Add the venues source
          if (!map.current.getSource('venues-source')) {
            map.current.addSource('venues-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: []
              },
              cluster: true,
              clusterMaxZoom: 14,
              clusterRadius: 50
            });
          }
          
          // Add the clusters layer
          if (!map.current.getLayer('clusters')) {
            map.current.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  20, 100,
                  30, 750,
                  40
                ]
              }
            });
          }

          // Add the cluster count layer
          if (!map.current.getLayer('cluster-count')) {
            map.current.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
              },
              paint: {
                'text-color': '#ffffff'
              }
            });
          }

          // Add the unclustered points layer
          if (!map.current.getLayer('unclustered-point')) {
            map.current.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
              }
            });
          }

          // Update venues on map
          updateVenuesOnMap();
        });
        break;
        
      case 'dark':
        const isDark = map.current.getStyle().name.includes('dark');
        map.current.setStyle(isDark ? 'mapbox://styles/mapbox/streets-v12' : 'mapbox://styles/mapbox/dark-v11');
        // Re-add sources and layers after style change
        map.current.once('style.load', () => {
          // Add the venues source
          if (!map.current.getSource('venues-source')) {
            map.current.addSource('venues-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: []
              },
              cluster: true,
              clusterMaxZoom: 14,
              clusterRadius: 50
            });
          }
          
          // Add the clusters layer
          if (!map.current.getLayer('clusters')) {
            map.current.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  20, 100,
                  30, 750,
                  40
                ]
              }
            });
          }

          // Add the cluster count layer
          if (!map.current.getLayer('cluster-count')) {
            map.current.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
              },
              paint: {
                'text-color': '#ffffff'
              }
            });
          }

          // Add the unclustered points layer
          if (!map.current.getLayer('unclustered-point')) {
            map.current.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
              }
            });
          }

          // Update venues on map
          updateVenuesOnMap();
        });
        break;
        
      case 'cluster':
        const clusterVisibility = map.current.getLayoutProperty('clusters', 'visibility');
        const newVisibility = clusterVisibility === 'visible' ? 'none' : 'visible';
        map.current.setLayoutProperty('clusters', 'visibility', newVisibility);
        map.current.setLayoutProperty('cluster-count', 'visibility', newVisibility);
        map.current.setLayoutProperty('unclustered-point', 'visibility', newVisibility === 'visible' ? 'none' : 'visible');
        break;
        
      case '3d':
        const pitch = map.current.getPitch();
        map.current.easeTo({
          pitch: pitch === 0 ? 45 : 0,
          duration: 1000
        });
        break;
    }
  };

  // Debounced update function for better performance
  const debouncedUpdateVenuesOnMap = useRef(_.debounce((venuesToShow) => {
    updateVenuesOnMap(venuesToShow);
  }, 300)).current;

  // Update venues on map when venues data changes
  useEffect(() => {
    if (map.current && mapLoaded) {
      const venuesToShow = displayMode === 'tour' ? tourVenues : venues;
      
      // Use debounced function to prevent too many updates
      debouncedUpdateVenuesOnMap(venuesToShow);
    }
  }, [venues, tourVenues, mapLoaded, displayMode]);

  // Update venues source with all venues or filtered venues based on display mode
  const ROUTE_COLORS = [
    '#2c7be5', // Light blue
    '#9b59b6', // Purple
    '#27ae60', // Green 
    '#3498db', // Darker blue
    '#8e44ad', // Dark purple
    '#16a085', // Sea green
    '#2980b9', // Navy blue
    '#2ecc71'  // Emerald
  ];

  // Add debounce utility function
  const debounce = (func, delay) => {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create a React ref for the debounced function
  const debouncedUpdateRef = useRef(null);
  
  // Initialize the debounced function in useEffect to avoid redeclaration
  useEffect(() => {
    debouncedUpdateRef.current = debounce(async (venuesToShow, tourDaysData, activeDayIdValue) => {
    if (!map.current || !map.current.getSource('venues-source')) return;

      console.log('Running debounced venue update...');
      // Clear existing routes (only if they exist to avoid unnecessary operations)
      try {
    const existingLayers = map.current.getStyle().layers;
    existingLayers.forEach(layer => {
          if (layer.id.startsWith('route-') && map.current.getLayer(layer.id)) {
        map.current.removeLayer(layer.id);
      }
    });
    
    const existingSources = Object.keys(map.current.getStyle().sources);
    existingSources.forEach(source => {
          if (source.startsWith('route-') && map.current.getSource(source)) {
        map.current.removeSource(source);
      }
    });
      } catch (error) {
        console.error('Error clearing existing routes:', error);
      }

      // Update venues source - limit to 500 visible venues for better performance
    const venuesToDisplay = venuesToShow || venues;
      
      // If there are too many venues, only show those in the current viewport
      let visibleVenues = venuesToDisplay;
      if (venuesToDisplay.length > 500 && map.current) {
        const bounds = map.current.getBounds();
        visibleVenues = venuesToDisplay.filter(venue => {
          if (!venue.latitude || !venue.longitude) return false;
          
          const lat = parseFloat(venue.latitude);
          const lng = parseFloat(venue.longitude);
          
          return (
            isValidLatitude(lat) &&
            isValidLongitude(lng) &&
            lat >= bounds.getSouth() &&
            lat <= bounds.getNorth() &&
            lng >= bounds.getWest() &&
            lng <= bounds.getEast()
          );
        }).slice(0, 500); // Limit to 500 for performance
      }
      
      // Create features with special property to identify tour venues
      const features = visibleVenues.map(venue => {
        // Check if this venue is part of the tour
        const isTourVenue = allTourVenues.some(tv => tv.id === venue.id);
        
        return {
      type: 'Feature',
      geometry: {
        type: 'Point',
            coordinates: [parseFloat(venue.longitude), parseFloat(venue.latitude)]
      },
      properties: {
        id: venue.id,
        name: venue.name,
        city: venue.city,
        state: venue.state,
        capacity: venue.capacity,
            affiliate: venue.affiliate,
            isTourVenue: isTourVenue // Mark venues in the tour
      }
        };
      });

    map.current.getSource('venues-source').setData({
      type: 'FeatureCollection',
        features: features.filter(f => 
          f.geometry.coordinates[0] && 
          f.geometry.coordinates[1] && 
          !isNaN(f.geometry.coordinates[0]) && 
          !isNaN(f.geometry.coordinates[1])
        )
    });

      // Get all venues from all days to check total number
      const allTourVenues = tourDaysData.flatMap(day => day.venues);
      const isTourActive = allTourVenues.length > 0;
      
      // If we're in tour mode or have tour venues, make sure we draw routes
      if (!isTourActive) {
        console.log('No tour venues, skipping route drawing');
        return; // Skip drawing routes if no tour venues
      }

      console.log(`Drawing routes for tour with ${allTourVenues.length} total venues`);
      
      // Prioritize active day but draw all days if we don't have too many venues
      const totalVenues = tourDaysData.reduce((count, day) => count + day.venues.length, 0);
      const shouldDrawAllRoutes = totalVenues <= 15;
      
      // Get days to process - either all days or just the active day
      const daysToProcess = shouldDrawAllRoutes ? 
        tourDaysData : 
        [tourDaysData.find(day => day.id === activeDayIdValue)].filter(Boolean);

    // Draw routes between venues for each day
      for (let dayIndex = 0; dayIndex < daysToProcess.length; dayIndex++) {
        const day = daysToProcess[dayIndex];
        if (!day) continue;
        
      const dayVenues = day.venues;
        const originalDayIndex = tourDaysData.findIndex(d => d.id === day.id);

        // Only draw routes if we have at least 2 venues
      if (dayVenues.length >= 2) {
          console.log(`Drawing routes for day ${day.name} with ${dayVenues.length} venues`);
          
          // Draw routes between each consecutive pair of venues
          const maxPairs = dayVenues.length - 1;
          
          for (let venueIndex = 0; venueIndex < maxPairs; venueIndex++) {
          const start = dayVenues[venueIndex];
          const end = dayVenues[venueIndex + 1];
          
            if (start && end && 
                start.longitude && start.latitude && 
                end.longitude && end.latitude && 
                !isNaN(parseFloat(start.longitude)) && 
                !isNaN(parseFloat(start.latitude)) &&
                !isNaN(parseFloat(end.longitude)) && 
                !isNaN(parseFloat(end.latitude))) {
              
            try {
                console.log(`Drawing route from ${start.name} to ${end.name}`);
                
                // Use a unique cache key to avoid redundant API calls
                const cacheKey = `${start.id}-${end.id}`;
                
                // Check if we already have this route in our routeCache
                let routeData = routeCache.current[cacheKey];
                
                // If not in cache, fetch it and store in cache
                if (!routeData) {
                  console.log(`Fetching new route data for ${start.name} to ${end.name}`);
                  routeData = await getDirections(
                    [parseFloat(start.longitude), parseFloat(start.latitude)],
                    [parseFloat(end.longitude), parseFloat(end.latitude)]
              );

                  // Save to cache for future use
              if (routeData && routeData.geometry) {
                    routeCache.current[cacheKey] = routeData;
                  }
                } else {
                  console.log(`Using cached route data for ${start.name} to ${end.name}`);
                }

                if (routeData && routeData.geometry) {
                  const sourceId = `route-${originalDayIndex}-${venueIndex}`;
                  const layerId = `route-layer-${originalDayIndex}-${venueIndex}`;

                // Add source if it doesn't exist
                if (!map.current.getSource(sourceId)) {
                  map.current.addSource(sourceId, {
                    type: 'geojson',
                    data: {
                      type: 'Feature',
                      geometry: routeData.geometry
                    }
                  });
                } else {
                  map.current.getSource(sourceId).setData({
                    type: 'Feature',
                    geometry: routeData.geometry
                  });
                }

                // Add layer if it doesn't exist
                if (!map.current.getLayer(layerId)) {
                  map.current.addLayer({
                    id: layerId,
                    type: 'line',
                    source: sourceId,
                    layout: {
                      'line-join': 'round',
                      'line-cap': 'round'
                    },
                    paint: {
                        'line-color': ROUTE_COLORS[originalDayIndex % ROUTE_COLORS.length],
                      'line-width': 3,
                      'line-opacity': 0.8
                    }
                  });
                }
              }
            } catch (error) {
              console.error('Error drawing route:', error);
            }
          }
        }
      }
      }
    }, 100); // Reduced debounce time from 300ms to 100ms for faster response
  }, []);
  
  // Wrapper function that calls the debounced version
  const updateVenuesOnMap = (venuesToShow = null) => {
    if (debouncedUpdateRef.current) {
      debouncedUpdateRef.current(venuesToShow, tourDays, activeDayId);
    }
  };

  const calculateDayRouteInfo = async (venues) => {
    if (!venues || venues.length < 2) {
      console.log('Not enough venues to calculate route info');
      return {
        totalDistance: 0,
        totalDuration: 0,
        fuelEstimate: 0,
        fuelCost: 0,
        legs: []
      };
    }
    
    console.log(`Calculating route info for ${venues.length} venues`);
    
    let totalDistance = 0;
    let totalDuration = 0;
    let totalFuelEstimate = 0;
    let totalFuelCost = 0;
    let legs = [];
    
    // Default values for fuel calculations - adjusted for more realistic values
    const avgFuelEfficiency = 25; // miles per gallon
    const avgGasPrice = 3.50; // dollars per gallon
    
    try {
      // Calculate route between each consecutive venue pair
      for (let i = 0; i < venues.length - 1; i++) {
        const start = venues[i];
        const end = venues[i + 1];
        
        if (isValidCoordinate(start) && isValidCoordinate(end)) {
          console.log(`Getting directions from ${start.name} to ${end.name}`);
          
          // Use a unique cache key for this route
          const cacheKey = `${start.id}-${end.id}`;
          let route;
          
          // Check if route is in cache
          if (routeCache.current[cacheKey]) {
            console.log('Using cached route data');
            route = routeCache.current[cacheKey];
          } else {
            // Get new route data
            route = await getDirections(
              [parseFloat(start.longitude), parseFloat(start.latitude)],
              [parseFloat(end.longitude), parseFloat(end.latitude)]
            );
            
            // Cache the route for future use
          if (route) {
              routeCache.current[cacheKey] = route;
            }
          }
          
          if (route) {
            const routeDistance = route.distance || 0; // This is in kilometers
            const routeDuration = route.duration || 0; // This is in seconds
            
            // Calculate fuel usage for this leg - Fixed calculation
            const distanceInMiles = routeDistance * 0.621371; // Convert km to miles
            const fuelUsed = distanceInMiles / avgFuelEfficiency; // Gallons of fuel
            const fuelCost = fuelUsed * avgGasPrice; // Cost in dollars
            
            console.log(`Leg fuel calculation: ${routeDistance}km = ${distanceInMiles}mi, ${fuelUsed} gal, $${fuelCost}`);
            
            // Store information about this leg
            legs.push({
              from: start.name,
              to: end.name,
              distance: Math.round(distanceInMiles * 10) / 10, // Round to 1 decimal
              duration: Math.round(routeDuration / 60), // Convert to minutes
              fuelUsed: Math.round(fuelUsed * 10) / 10, // Round to 1 decimal
              fuelCost: Math.round(fuelCost * 100) / 100 // Round to 2 decimals
            });
            
            // Add to totals
            totalDistance += routeDistance;
            totalDuration += routeDuration;
            totalFuelEstimate += fuelUsed;
            totalFuelCost += fuelCost;
          } else {
            console.log(`No route found between ${start.name} and ${end.name}`);
          }
        } else {
          console.log(`Invalid coordinates for ${start.name} or ${end.name}`);
          }
        }
      
      // Fix the output result to maintain distance in kilometers but also include miles
      const result = {
        totalDistance: Math.round(totalDistance), // In kilometers
        totalDistanceMiles: Math.round(totalDistance * 0.621371), // Miles conversion
        totalDuration: Math.round(totalDuration / 60), // Convert to minutes
        fuelEstimate: Math.round(totalFuelEstimate * 10) / 10, // Round to 1 decimal
        fuelCost: Math.round(totalFuelCost * 100) / 100, // Round to cents
        legs: legs
      };
      
      console.log('Route info calculated:', result);
      return result;
    } catch (error) {
      console.error('Error calculating route info:', error);
      return {
        totalDistance: 0,
        totalDistanceMiles: 0,
        totalDuration: 0,
        fuelEstimate: 0,
        fuelCost: 0,
        legs: []
      };
    }
  };

  const renderDayRouteInfo = (day, index) => {
    if (!day || !day.venues || !Array.isArray(day.venues) || day.venues.length < 2) {
      return null;
    }
    
    const routeInfo = calculateDayRouteInfo(day.venues);
    if (!routeInfo || !routeInfo.legs || !Array.isArray(routeInfo.legs)) {
      return null;
    }

    const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
    
    return (
      <View style={styles.dayRouteInfo}>
        {routeInfo.legs.map((leg, i) => (
          <View key={i} style={styles.dayRouteLeg}>
            <Text style={styles.dayRouteText}>
              {leg.from} → {leg.to}
            </Text>
            <Text style={styles.dayRouteMileage}>
              {leg.distance} miles ({Math.round(leg.distance / 0.621371)} km)
            </Text>
          </View>
        ))}
        <Text style={styles.totalMileage}>
          Total: {routeInfo.totalDistanceMiles} miles ({routeInfo.totalDistance} km)
        </Text>
        <Text style={styles.fuelEstimateText}>
          Fuel: {routeInfo.fuelEstimate} gal (${routeInfo.fuelCost})
        </Text>
      </View>
    );
  };

  // Update tour route on map
  const updateTourRoute = () => {
    if (!map.current || tourVenues.length < 2) return;
    
    // Draw the route line
    calculateRoute();
    
    // Fit map to tour venues
    const bounds = new mapboxgl.LngLatBounds();
    tourVenues.forEach(venue => {
      if (venue.longitude && venue.latitude) {
        bounds.extend([venue.longitude, venue.latitude]);
      }
    });
    
    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, {
        padding: 70,
        maxZoom: 12
      });
    }
  };

  // Add renderBottomControls function before the return statement
  const renderBottomControls = () => (
    <View style={styles.bottomRightControls}>
      <TouchableOpacity
        style={styles.controlButton}
        onPress={() => toggleDisplayMode('satellite')}
      >
        <SatelliteIcon />
      </TouchableOpacity>
      
      <TouchableOpacity
        style={styles.controlButton}
        onPress={() => toggleDisplayMode('dark')}
      >
        <DarkModeIcon />
      </TouchableOpacity>
      
      <TouchableOpacity
        style={styles.controlButton}
        onPress={() => toggleDisplayMode('cluster')}
      >
        <ClusterIcon />
      </TouchableOpacity>
      
      <TouchableOpacity
        style={styles.controlButton}
        onPress={() => toggleDisplayMode('3d')}
      >
        <ThreeDIcon />
      </TouchableOpacity>
    </View>
  );

  // Add renderLocationPrompt function since it's used in the JSX
  const renderLocationPrompt = () => {
    if (!locationRequested && !userLocation && !locationPermissionDenied) {
      return (
        <View style={styles.locationPrompt}>
          <Text style={styles.locationPromptText}>
            Enable location services to see venues near you and get accurate distance calculations
          </Text>
          <TouchableOpacity
            style={styles.locationPromptButton}
            onPress={requestUserLocation}
          >
            <Text style={styles.locationPromptButtonText}>Enable Location</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return null;
  };

  // Add function to add venue to specific day and update routes
  const addVenueToDayById = (venue, dayId) => {
    console.log(`Adding venue ${venue.name} to day ${dayId}`);
    
    setTourDays(prevDays => {
      // Find the day we're adding to
      const updatedDay = prevDays.find(d => d.id === dayId);
      
      if (!updatedDay) {
        console.error(`Day with ID ${dayId} not found`);
        return prevDays;
      }
      
      // Check if there will be at least 2 venues after adding the new venue
      const willHaveMultipleVenues = updatedDay.venues.length >= 1;
      
      // Update the days with the new venue
      const newDays = prevDays.map(day =>
        day.id === dayId
          ? { ...day, venues: [...day.venues, venue] }
          : day
      );
      
      // Immediately force display mode to tour if we'll have enough venues for a route
      if (willHaveMultipleVenues && displayMode !== 'tour') {
        console.log('Setting display mode to tour for route visibility');
        setDisplayMode('tour');
      }
      
      // Immediately trigger route update - don't wait for the timeout
      const allVenues = newDays.flatMap(day => day.venues);
      
      // Schedule immediate map update
      requestAnimationFrame(() => {
        // Force routes to appear by showing tour venues
        if (map.current && mapLoaded) {
          console.log('Forcing immediate map update to show routes');
          updateVenuesOnMap(allVenues);
          
          // Calculate route information for display
          calculateTourDistances().then(info => {
            if (info) {
              console.log('Route info calculated:', info);
              setRouteInfo(info);
            } else {
              console.error('Failed to calculate route info');
            }
          });
        }
      });
      
      return newDays;
    });
  };

  // Update the venue card distance text
  const renderVenueDistance = (venue) => {
    if (!userLocation || !venue.latitude || !venue.longitude) return null;
    
    const distance = calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      venue.latitude,
      venue.longitude
    ) * 0.621371; // Convert to miles
    
    return (
      <Text style={styles.mileageText}>
        {distance.toFixed(1)} miles away
      </Text>
    );
  };

  // Add new function to fetch POIs from Overpass API
  const fetchPOIs = async () => {
    if (!map.current || loadingPOIs) return;

    setLoadingPOIs(true);
    const bounds = map.current.getBounds();
    const [west, south, east, north] = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth()
    ];

    try {
      const query = `
        [out:json][timeout:25];
        (
          way["tourism"="hotel"](${south},${west},${north},${east});
          node["tourism"="hotel"](${south},${west},${north},${east});
          way["amenity"="fuel"](${south},${west},${north},${east});
          node["amenity"="fuel"](${south},${west},${north},${east});
        );
        out center;
      `;

      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query
      });

      const data = await response.json();
      
      // Process hotels and gas stations
      const hotelsFeatures = [];
      const gasFeatures = [];

      data.elements.forEach(element => {
        const coordinates = element.type === 'node' 
          ? [element.lon, element.lat]
          : [element.center.lon, element.center.lat];

        const feature = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates
          },
          properties: {
            id: element.id,
            name: element.tags.name || '',
            address: element.tags['addr:street'] 
              ? `${element.tags['addr:street']} ${element.tags['addr:housenumber'] || ''}`
              : ''
          }
        };

        if (element.tags.tourism === 'hotel') {
          hotelsFeatures.push(feature);
        } else if (element.tags.amenity === 'fuel') {
          gasFeatures.push(feature);
        }
      });

      // Update map sources
      if (map.current.getSource('hotels-source')) {
        map.current.getSource('hotels-source').setData({
          type: 'FeatureCollection',
          features: hotelsFeatures
        });
      }

      if (map.current.getSource('gas-source')) {
        map.current.getSource('gas-source').setData({
          type: 'FeatureCollection',
          features: gasFeatures
        });
      }

      setHotels(hotelsFeatures);
      setGasStations(gasFeatures);
    } catch (error) {
      console.error('Error fetching POIs:', error);
    } finally {
      setLoadingPOIs(false);
    }
  };

  // Add event listener for map movement
  useEffect(() => {
    if (map.current) {
      const debouncedFetchPOIs = _.debounce(fetchPOIs, 1000);
      map.current.on('moveend', debouncedFetchPOIs);
      
      return () => {
        if (map.current) {
          map.current.off('moveend', debouncedFetchPOIs);
        }
      };
    }
  }, [map.current]);

  // Add separate effect for handling mode changes
  useEffect(() => {
    const drawControls = document.querySelector('.mapboxgl-ctrl-group');
    if (drawControls) {
      drawControls.style.display = mapMode === 'draw' ? 'block' : 'none';
    }
  }, [mapMode]);

  // Add mode controls to the map container
  const renderModeControls = () => (
    <View style={styles.modeControls}>
      <TouchableOpacity
        style={[
          styles.modeButton,
          mapMode === 'travel' && styles.modeButtonActive
        ]}
        onPress={() => setMapMode('travel')}
      >
        <Text style={styles.modeButtonText}>Travel Mode</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.modeButton,
          styles.modeButtonDisabled
        ]}
        disabled={true}
      >
        <Text style={styles.modeButtonText}>Draw Mode</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.modeButton,
          styles.modeButtonDisabled
        ]}
        disabled={true}
      >
        <Text style={styles.modeButtonText}>Data Mode</Text>
      </TouchableOpacity>
    </View>
  );

  // Add draw instructions component
  const renderDrawInstructions = () => {
    if (mapMode !== 'draw') return null;

    return (
      <View style={styles.drawInstructions}>
        <Text style={styles.drawInstructionsText}>
          1. Click the polygon icon in the top right to start drawing{'\n'}
          2. Click on the map to place points{'\n'}
          3. Connect back to the first point to complete{'\n'}
          4. Use the trash icon to clear and start over
        </Text>
      </View>
    );
  };

  // Add state for drawing status
  const [isDrawing, setIsDrawing] = useState(false);

  // Add draw mode controls component
  const renderDrawControls = () => {
    if (mapMode !== 'draw') return null;

    return (
      <View style={styles.drawModeContainer}>
        <Text style={styles.drawModeInstructions}>
          {isDrawing 
            ? "Click on the map to place points. Connect back to the first point to complete the boundary." 
            : "Click 'Start Drawing' to begin creating a boundary. Only venues within the boundary will be shown."}
        </Text>
        <TouchableOpacity
          style={[styles.drawButton, isDrawing && styles.drawButtonActive]}
          onPress={() => {
            if (draw) {
              if (isDrawing) {
                draw.trash();
                setIsDrawing(false);
              } else {
                draw.changeMode('draw_polygon');
                setIsDrawing(true);
              }
            }
          }}
        >
          <Text style={styles.drawButtonText}>
            {isDrawing ? 'Cancel Drawing' : 'Start Drawing'}
          </Text>
        </TouchableOpacity>
        {drawBoundary && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={() => {
              if (draw) {
                draw.deleteAll();
                setDrawBoundary(null);
                updateVenuesOnMap(venues);
                setIsDrawing(false);
              }
            }}
          >
            <Text style={styles.clearButtonText}>Clear Boundary</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // Update map event handlers
  useEffect(() => {
    if (!map.current || !draw) return;

    const handleDrawCreate = (e) => {
      if (mapMode === 'draw') {
        setDrawBoundary(e.features[0]);
        const polygon = turf.polygon(e.features[0].geometry.coordinates);
        const filteredVenues = venues.filter(venue => {
          if (!venue.latitude || !venue.longitude) return false;
          const point = turf.point([venue.longitude, venue.latitude]);
          return turf.booleanPointInPolygon(point, polygon);
        });
        updateVenuesOnMap(filteredVenues);
        setIsDrawing(false);
      }
    };

    const handleDrawCancel = () => {
      setIsDrawing(false);
    };

    map.current.on('draw.create', handleDrawCreate);
    map.current.on('draw.cancel', handleDrawCancel);

    return () => {
      if (map.current) {
        map.current.off('draw.create', handleDrawCreate);
        map.current.off('draw.cancel', handleDrawCancel);
      }
    };
  }, [map.current, draw, mapMode, venues]);

  // Add new state for tour management
  const [savedTours, setSavedTours] = useState([]);
  const [currentTourId, setCurrentTourId] = useState(null);

  // Add state for toast
  const [toast, setToast] = useState(null);

  // Update the saveTour function
  const saveTour = async () => {
    if (!auth.currentUser) {
      setToast({ message: 'Please log in to save tours', type: 'error' });
      return;
    }

    try {
      setToast({ message: 'Saving tour...', type: 'warning' });
      const db = getFirestore();
      const tourData = {
        name: tourName,
        days: tourDays,
        updatedAt: new Date(),
      };

      if (!currentTourId) {
        // New tour
        tourData.createdAt = new Date();
        const toursCollectionRef = collection(db, 'users', auth.currentUser.uid, 'tours');
        const docRef = await addDoc(toursCollectionRef, tourData);
        setCurrentTourId(docRef.id);
        setToast({ message: 'Tour saved successfully!', type: 'success' });
      } else {
        // Update existing tour
        const tourRef = doc(db, 'users', auth.currentUser.uid, 'tours', currentTourId);
        await updateDoc(tourRef, tourData);
        setToast({ message: 'Tour updated successfully!', type: 'success' });
      }
    } catch (error) {
      console.error('Error saving tour:', error);
      setToast({ message: 'Failed to save tour. Please try again.', type: 'error' });
    }
  };

  // Add function to load saved tours
  const loadSavedTours = async () => {
    if (!auth.currentUser) return;

    try {
      const db = getFirestore();
      const toursCollectionRef = collection(db, 'users', auth.currentUser.uid, 'tours');
      const toursSnapshot = await getDocs(toursCollectionRef);
      
      const tours = toursSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSavedTours(tours);
    } catch (error) {
      console.error('Error loading tours:', error);
    }
  };

  // Add function to load a specific tour
  const loadTour = async (tourId) => {
    try {
      const db = getFirestore();
      const tourRef = doc(db, 'users', auth.currentUser.uid, 'tours', tourId);
      const tourDoc = await getDoc(tourRef);
      
      if (tourDoc.exists()) {
        const tourData = tourDoc.data();
        setTourName(tourData.name);
        setTourDays(tourData.days);
        setCurrentTourId(tourId);
        
        // Update venues on map
        const allVenues = tourData.days.flatMap(day => day.venues);
        if (allVenues.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          allVenues.forEach(venue => {
            if (venue.longitude && venue.latitude) {
              bounds.extend([venue.longitude, venue.latitude]);
            }
          });
          
          if (!bounds.isEmpty()) {
            map.current?.fitBounds(bounds, {
              padding: 50,
              maxZoom: 12
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading tour:', error);
      Alert.alert('Error', 'Failed to load tour. Please try again.');
    }
  };

  // Add tour management controls to the header
  const renderTourControls = () => (
    <View style={styles.tourControls}>
      <TouchableOpacity
        style={styles.newTourButton}
        onPress={() => {
          if (currentTourId || (tourName !== 'My Tour' || tourDays[0].venues.length > 0)) {
            Alert.alert(
              'Create New Tour',
              'Are you sure you want to start a new tour? Any unsaved changes will be lost.',
              [
                {
                  text: 'Cancel',
                  style: 'cancel'
                },
                {
                  text: 'Create New',
                  onPress: createNewTour
                }
              ]
            );
          } else {
            createNewTour();
          }
        }}
      >
        <Text style={styles.newTourButtonText}>New Tour</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.saveButton}
        onPress={saveTour}
      >
        <Text style={styles.saveButtonText}>Save Tour</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.shareButton}
        onPress={shareTour}
      >
        <Text style={styles.shareButtonText}>Share Tour</Text>
      </TouchableOpacity>
    </View>
  );

  // Load saved tours when component mounts
  useEffect(() => {
    loadSavedTours();
  }, [auth.currentUser]);

  // Add to the existing useEffect that handles initialization
  useEffect(() => {
    const loadTourFromParams = async () => {
      if (route.params?.loadTourId && auth.currentUser) {
        try {
          const db = getFirestore();
          const tourRef = doc(db, 'users', auth.currentUser.uid, 'tours', route.params.loadTourId);
          const tourDoc = await getDoc(tourRef);
          
          if (tourDoc.exists()) {
            const tourData = tourDoc.data();
            setTourName(tourData.name);
            setTourDays(tourData.days);
            setCurrentTourId(route.params.loadTourId);
            
            // Update venues on map
            const allVenues = tourData.days.flatMap(day => day.venues);
            if (allVenues.length > 0) {
              const bounds = new mapboxgl.LngLatBounds();
              allVenues.forEach(venue => {
                if (venue.longitude && venue.latitude) {
                  bounds.extend([venue.longitude, venue.latitude]);
                }
              });
              
              if (!bounds.isEmpty() && map.current) {
                map.current.fitBounds(bounds, {
                  padding: 50,
                  maxZoom: 12
                });
              }
            }
          }
        } catch (error) {
          console.error('Error loading tour:', error);
          Alert.alert('Error', 'Failed to load tour. Please try again.');
        }
      }
    };

    loadTourFromParams();
  }, [route.params?.loadTourId, auth.currentUser]);

  // Add clipboard functionality
  const shareTour = () => {
    if (!currentTourId) {
      setToast({ message: 'Please save your tour first', type: 'warning' });
      return;
    }

    const shareUrl = `${window.location.origin}/tour/${currentTourId}`;
    Clipboard.setString(shareUrl);
    setToast({ message: 'Tour link copied to clipboard!', type: 'success' });
  };

  // Update the new tour function to completely reset state
  const createNewTour = () => {
    setTourName('My Tour');
    setTourDays([{ id: 'day-1', name: 'Day 1', venues: [] }]);
    setCurrentTourId(null);
    // Reset map to initial state
    if (map.current) {
      map.current.flyTo({
        center: initialCenter,
        zoom: 3,
        essential: true
      });
    }
  };

  // Add state for selected venue
  const [selectedVenueId, setSelectedVenueId] = useState(null);

  // Add function to handle venue selection and zoom
  const handleVenueSelect = (venue) => {
    if (!venue.latitude || !venue.longitude || !map.current) return;

    // Update selected venue state
    setSelectedVenueId(venue.id);

    // Fly to venue location
    map.current.flyTo({
      center: [venue.longitude, venue.latitude],
      zoom: 15,
      essential: true
    });

    // Show popup for the venue
    new mapboxgl.Popup({
      closeOnClick: false,
      closeButton: true,
      className: 'dark-popup' // Add this class for styling
    })
      .setLngLat([venue.longitude, venue.latitude])
      .setHTML(`
        <div style="
          color: #fff;
          background-color: #2c2c2c;
          padding: 15px;
          border-radius: 8px;
          min-width: 200px;
        ">
          <h3 style="margin: 0 0 8px 0; font-size: 16px;">${venue.name}</h3>
          <p style="margin: 0 0 4px 0; font-size: 14px;">${venue.city}, ${venue.state}</p>
          <p style="margin: 0 0 4px 0; font-size: 14px;">Capacity: ${venue.capacity || 'N/A'}</p>
          <p style="margin: 0 0 8px 0; font-size: 14px;">Type: ${venue.venueType || 'N/A'}</p>
          <div style="
            display: flex;
            gap: 8px;
            margin-top: 12px;
          ">
            <button 
              onclick="window.handleMapPopupAction('addToTour', '${venue.id}')" 
              style="
                background-color: #e67e22;
                color: white;
                border: none;
                padding: 8px 16px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                flex: 1;
                transition: background-color 0.2s ease;
              "
              onmouseover="this.style.backgroundColor='#d35400'"
              onmouseout="this.style.backgroundColor='#e67e22'"
            >
              Add to Tour
            </button>
            <button 
              onclick="window.handleMapPopupAction('viewDetails', '${venue.id}')" 
              style="
                background-color: #2c7be5;
                color: white;
                border: none;
                padding: 8px 16px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                flex: 1;
                transition: background-color 0.2s ease;
              "
              onmouseover="this.style.backgroundColor='#1b69c2'"
              onmouseout="this.style.backgroundColor='#2c7be5'"
            >
              View Details
            </button>
          </div>
        </div>
      `)
      .addTo(map.current);
  };

  // Add this after the other useEffect hooks
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.handleMapPopupAction = (action, venueId) => {
        const venue = venues.find(v => v.id === venueId);
        if (!venue) return;

        switch (action) {
          case 'addToTour':
            addVenueToTour(venue);
            // Close any open popups
            const popups = document.getElementsByClassName('mapboxgl-popup');
            if (popups.length) {
              popups[0].remove();
            }
            break;
          case 'viewDetails':
            navigation.navigate('Venue', { venueId });
            break;
        }
      };
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.handleMapPopupAction;
      }
    };
  }, [venues, navigation, addVenueToTour]);

  const calculateTourDistances = async () => {
    let totalDistance = 0;
    let totalDuration = 0;
    let totalFuelUsed = 0;
    let totalFuelCost = 0;
    
    try {
      // Calculate route info for each day
      for (const day of tourDays) {
        const dayRouteInfo = await calculateDayRouteInfo(day.venues);
        if (dayRouteInfo) {
          totalDistance += dayRouteInfo.totalDistance;
          totalDuration += dayRouteInfo.totalDuration;
          totalFuelUsed += dayRouteInfo.fuelEstimate;
          totalFuelCost += dayRouteInfo.fuelCost;
        }
      }
      
      return {
        totalDistance: Math.round(totalDistance),
        totalDuration: Math.round(totalDuration),
        totalFuelUsed: Math.round(totalFuelUsed * 100) / 100,
        totalFuelCost: Math.round(totalFuelCost * 100) / 100
      };
    } catch (error) {
      console.error('Error calculating tour distances:', error);
      return {
        totalDistance: 0,
        totalDuration: 0,
        totalFuelUsed: 0,
        totalFuelCost: 0
      };
    }
  };

  // Function to filter venues based on search query
  const filterVenues = (query) => {
    if (!query || query.trim() === '') {
      // If no search query, return limited number of venues
      return venues.slice(0, 100);
    }
    
    const searchQuery = query.toLowerCase().trim();
    const searchTerms = searchQuery.split(' ').filter(term => term.length > 0);
    
    console.log(`Searching venues with terms: ${searchTerms.join(', ')}`);
    
    // First find exact matches
    const exactMatches = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase() || '';
      return venueName === searchQuery;
    });
    
    if (exactMatches.length > 0) {
      return exactMatches;
    }
    
    // Then find starts-with matches
    const startsWithMatches = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase() || '';
      return venueName.startsWith(searchQuery);
    });
    
    if (startsWithMatches.length > 0) {
      return startsWithMatches;
    }
    
    // Finally, do partial matching
    const partialMatches = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase() || '';
      const venueCity = venue.city?.toLowerCase() || '';
      const venueState = venue.state?.toLowerCase() || '';
      
      return searchTerms.every(term => 
        venueName.includes(term) || 
        venueCity.includes(term) || 
        venueState.includes(term)
      );
    });
    
    return partialMatches.slice(0, 100); // Limit to 100 results for performance
  };
  
  // Create a debounced version of search for better performance
  const debouncedSearchRef = useRef(null);
  
  useEffect(() => {
    // Initialize the debounced search function
    debouncedSearchRef.current = debounce((text) => {
      const filtered = filterVenues(text);
      setDisplayedVenues(filtered);
    }, 300); // 300ms delay
  }, [venues]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Tour Planner</Text>
        <View style={styles.tourNameContainer}>
          <TextInput
            style={styles.tourNameInput}
            value={tourName}
            onChangeText={setTourName}
            placeholder="Enter Tour Name"
            placeholderTextColor="#666"
          />
        </View>
        {renderTourControls()}
      </View>

      <View style={styles.contentContainer}>
        {/* Left Panel - Available Venues */}
        <Animated.View style={[
          styles.venueList,
          {
            width: venueListVisible ? 300 : 0,
            opacity: venueListAnim,
            transform: [{
              translateX: venueListAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [-300, 0]
              })
            }]
          }
        ]}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Available Venues</Text>
            <Text style={styles.venueCountText}>
              {displayedVenues.length} venues {venues.length > 100 && `(of ${venues.length} total)`}
            </Text>
            
            {/* Add filters container */}
            <View style={styles.filtersContainer}>
              <View style={styles.filterRow}>
                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Capacity</Text>
                  <TouchableOpacity
                    style={styles.filterButton}
                    onPress={() => setFilters(prev => ({
                      ...prev,
                      capacity: prev.capacity === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <Text style={styles.filterButtonText}>
                      {filters.capacity === 'asc' ? '↑ Ascending' : '↓ Descending'}
                    </Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>State</Text>
                  <TouchableOpacity
                    style={styles.filterButton}
                    onPress={() => setFilters(prev => ({
                      ...prev,
                      state: prev.state === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <Text style={styles.filterButtonText}>
                      {filters.state === 'asc' ? 'A-Z' : 'Z-A'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            <TextInput
              style={styles.searchInput}
              value={searchQuery}
              onChangeText={(text) => {
                setSearchQuery(text);
                // Use debounced search to avoid performance issues while typing
                if (debouncedSearchRef.current) {
                  debouncedSearchRef.current(text);
                }
              }}
              placeholder="Search venues by name, city, or state..."
              placeholderTextColor="#666"
            />
          </View>

          {/* Show cluster venues if any are selected */}
          {clusterVenues.length > 0 && (
            <View style={styles.clusterSection}>
              <Text style={styles.clusterTitle}>
                Selected Cluster ({clusterVenues.length} venues)
              </Text>
            </View>
          )}

          <ScrollView 
            style={styles.venueListScroll}
            contentContainerStyle={{ paddingBottom: 20 }}
            removeClippedSubviews={true} // Optimize memory for scrolling
            initialNumToRender={20} // Render fewer items initially
            maxToRenderPerBatch={10} // Limit batch size for smoother scrolling
            windowSize={10} // Reduce window size for better performance
            updateCellsBatchingPeriod={50} // Update cells less frequently
          >
            {displayedVenues.map((venue, index) => {
              // Early return if venue doesn't have required properties
              if (!venue.id || !venue.name) return null;
              
              // Simple venue card rendering
              return (
              <TouchableOpacity
                key={venue.id}
                style={[
                  styles.venueCard,
                    selectedVenueId === venue.id && styles.selectedVenueCard,
                    index % 2 === 0 && { backgroundColor: 'rgba(0,0,0,0.2)' } // Alternate background colors for better visibility
                ]}
                onPress={() => handleVenueSelect(venue)}
              >
                <View style={styles.venueInfo}>
                  <Text style={styles.venueName}>{venue.name}</Text>
                  <Text style={styles.venueLocation}>
                    {venue.city}, {venue.state}
                  </Text>
                  {renderVenueDistance(venue)}
                  <View style={styles.venueDetails}>
                    <Text style={styles.detailText}>
                      Capacity: {venue.capacity || 'N/A'}
                    </Text>
                    <Text style={styles.detailText}>
                      Type: {venue.venueType || 'N/A'}
                    </Text>
                  </View>
                  <View style={styles.buttonGroup}>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.primaryButton]}
                      onPress={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent onPress
                        addVenueToTour(venue);
                      }}
                    >
                      <Text style={styles.buttonText}>Add to Tour</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.secondaryButton]}
                      onPress={(e) => {
                        e.stopPropagation();
                        navigation.navigate('Venue', { venueId: venue.id });
                      }}
                    >
                      <Text style={styles.buttonText}>View Details</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </TouchableOpacity>
              );
            })}
          </ScrollView>
          
          {/* Toggle button for venue list */}
          <TouchableOpacity
            style={[styles.toggleButton, styles.leftToggle]}
            onPress={toggleVenueList}
          >
            <Text style={styles.toggleButtonText}>
              {venueListVisible ? '←' : '→'}
            </Text>
          </TouchableOpacity>
        </Animated.View>

        {/* Middle - Map (Main Component) */}
        <View style={styles.mapContainer}>
          {Platform.OS === 'web' ? (
            <>
              <div ref={mapContainer} style={{width: '100%', height: '100%', borderRadius: 8}} />
              {renderModeControls()}
              {renderDrawControls()}
              <TouchableOpacity 
                style={styles.locationButtonContainer}
                onPress={requestUserLocation}
              >
                <View style={styles.locationButton}>
                  <LocationIcon style={styles.buttonIcon} />
                </View>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.recenterButton}
                onPress={recenterMap}
              >
                <RecenterIcon style={styles.buttonIcon} />
              </TouchableOpacity>

              {renderBottomControls()}
              
              <View style={styles.statsOverlay}>
                <Text style={styles.statsText}>
                  {displayedVenues.length} of {venues.length} venues displayed
                </Text>
                {userLocation && (
                  <Text style={styles.statsText}>
                    Location tracking enabled
                  </Text>
                )}
              </View>
            </>
          ) : (
            <Text style={styles.mobileMapText}>
              Map view is available on the web version
            </Text>
          )}
        </View>

        {/* Right Panel - Tour Stops */}
        <Animated.View style={[
          styles.venueList,
          {
            width: tourStopsVisible ? 300 : 0,
            opacity: tourStopsAnim,
            transform: [{
              translateX: tourStopsAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [300, 0]
              })
            }]
          }
        ]}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Tour Stops</Text>
            <Text style={styles.venueCountText}>{tourVenues.length} venues selected</Text>
          </View>

          <ScrollView style={styles.tourVenuesList}>
            {tourDays.map((day, dayIndex) => (
              <View
                key={day.id}
                style={[
                  styles.dayContainer,
                  activeDayId === day.id && styles.activeDayContainer
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.dayHeader,
                    activeDayId === day.id && styles.activeDayHeader
                  ]}
                  onPress={() => setActiveDayId(day.id)}
                >
                  <View style={styles.dayTitleContainer}>
                    {activeDayId === day.id && (
                      <Text style={styles.activeDayIndicator}>●</Text>
                    )}
                    {editingDayId === day.id ? (
                      <View style={styles.dayEditContainer}>
                        <TextInput
                          style={styles.dayNameInput}
                          value={newDayName}
                          onChangeText={setNewDayName}
                          autoFocus
                        />
                        <TouchableOpacity
                          style={styles.editDayButton}
                          onPress={() => {
                            setTourDays(days =>
                              days.map(d =>
                                d.id === day.id
                                  ? { ...d, name: newDayName }
                                  : d
                              )
                            );
                            setEditingDayId(null);
                          }}
                        >
                          <Text style={styles.saveIcon}>✓</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <>
                        <Text style={[
                          styles.dayTitle,
                          activeDayId === day.id && styles.activeDayTitle
                        ]}>
                          {day.name}
                        </Text>
                        <TouchableOpacity
                          style={styles.editDayButton}
                          onPress={() => {
                            setEditingDayId(day.id);
                            setNewDayName(day.name);
                          }}
                        >
                          <Text style={styles.editIcon}>✎</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                  {tourDays.length > 1 && (
                    <TouchableOpacity
                      style={styles.removeDayButton}
                      onPress={() => {
                        setTourDays(days => days.filter(d => d.id !== day.id));
                        if (activeDayId === day.id) {
                          setActiveDayId(tourDays[0].id);
                        }
                      }}
                    >
                      <Text style={styles.removeDayButtonText}>×</Text>
                    </TouchableOpacity>
                  )}
                </TouchableOpacity>

                {day.venues.length === 0 ? (
                  <View style={[
                    styles.emptyDayContainer,
                    activeDayId === day.id && styles.activeEmptyDayContainer
                  ]}>
                    <Text style={styles.emptyDayText}>
                      Drop venues here
                    </Text>
                  </View>
                ) : (
                  <>
                    {day.venues.map((venue, index) => (
                      <View
                        key={venue.id}
                        style={[
                          styles.tourVenueCard,
                          isDragging === venue.id && styles.dragging,
                          activeDayId === day.id && styles.activeVenueCard,
                          selectedVenueId === venue.id && styles.selectedVenueCard
                        ]}
                      >
                        <TouchableOpacity
                          style={styles.tourVenueHeader}
                          onPress={() => handleVenueSelect(venue)}
                        >
                          <Text style={[
                            styles.tourVenueIndex,
                            activeDayId === day.id && styles.activeTourVenueIndex
                          ]}>
                            {index + 1}
                          </Text>
                          <View style={styles.tourVenueInfo}>
                            <Text style={styles.tourVenueName}>{venue.name}</Text>
                            <Text style={styles.tourVenueLocation}>
                              {venue.city}, {venue.state}
                            </Text>
                          </View>
                        </TouchableOpacity>
                        <View style={styles.buttonGroup}>
                          <TouchableOpacity
                            style={[styles.actionButton, styles.secondaryButton]}
                            onPress={() => navigation.navigate('Venue', { venueId: venue.id })}
                          >
                            <Text style={styles.buttonText}>View Details</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.actionButton, styles.dangerButton]}
                            onPress={() => {
                              setTourVenues(prev => 
                                prev.filter(v => v.id !== venue.id)
                              );
                              setTourDays(days =>
                                days.map(d => ({
                                  ...d,
                                  venues: d.venues.filter(v => v.id !== venue.id)
                                }))
                              );
                            }}
                          >
                            <Text style={styles.buttonText}>Remove from Tour</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                    
                    {/* Fix the index reference here */}
                    {renderDayRouteInfo(day, dayIndex)}
                  </>
                )}
              </View>
            ))}

            <TouchableOpacity
              style={styles.addDayButton}
              onPress={() => {
                const newDayId = `day-${tourDays.length + 1}`;
                setTourDays(prev => [
                  ...prev,
                  {
                    id: newDayId,
                    name: `Day ${tourDays.length + 1}`,
                    venues: []
                  }
                ]);
              }}
            >
              <Text style={styles.addDayButtonText}>+ Add Day</Text>
            </TouchableOpacity>
          </ScrollView>

          {/* Route information */}
          {tourVenues.length >= 2 && (
            <View style={styles.routeInfo}>
              <Text style={styles.routeInfoTitle}>Route Information</Text>
              <React.Suspense fallback={
                <Text style={styles.routeInfoText}>Calculating route information...</Text>
              }>
                <RouteInfoDisplay tourDays={tourDays} />
              </React.Suspense>
            </View>
          )}

          {/* Toggle button for tour stops */}
          <TouchableOpacity
            style={[styles.toggleButton, styles.rightToggle]}
            onPress={toggleTourStops}
          >
            <Text style={styles.toggleButtonText}>
              {tourStopsVisible ? '→' : '←'}
            </Text>
          </TouchableOpacity>
        </Animated.View>
      </View>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onHide={() => setToast(null)}
        />
      )}
    </View>
  );
}

// Add the RouteInfoDisplay component
const RouteInfoDisplay = React.memo(({ tourDays }) => {
  const [routeInfo, setRouteInfo] = useState({
    totalDistance: 0,
    totalDuration: 0,
    totalFuelUsed: 0,
    totalFuelCost: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchRouteInfo = async () => {
      setIsLoading(true);
      try {
        let totalDistance = 0;
        let totalDuration = 0;
        let totalFuelUsed = 0;
        let totalFuelCost = 0;
        
        // Calculate route info for each day
        for (const day of tourDays) {
          if (day.venues.length >= 2) {
            for (let i = 0; i < day.venues.length - 1; i++) {
              const start = day.venues[i];
              const end = day.venues[i + 1];
              
              if (start.latitude && start.longitude && end.latitude && end.longitude) {
                const route = await getDirections(
                  { lat: parseFloat(start.latitude), lng: parseFloat(start.longitude) },
                  { lat: parseFloat(end.latitude), lng: parseFloat(end.longitude) }
                );
                
                if (route && isMounted) {
                  totalDistance += route.distance || 0;
                  totalDuration += route.duration || 0;
                  
                  // Calculate fuel usage
                  const distanceInMiles = (route.distance || 0) * 0.621371; // Convert km to miles
                  const fuelUsed = distanceInMiles / 25; // Assuming 25 mpg average
                  totalFuelUsed += fuelUsed;
                  totalFuelCost += fuelUsed * 3.50; // Assuming $3.50 per gallon
                }
              }
            }
          }
        }

        if (isMounted) {
          setRouteInfo({
            totalDistance: Math.round(totalDistance),
            totalDuration: Math.round(totalDuration),
            totalFuelUsed: Math.round(totalFuelUsed * 100) / 100,
            totalFuelCost: Math.round(totalFuelCost * 100) / 100
          });
        }
      } catch (error) {
        console.error('Error calculating route info:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchRouteInfo();
    return () => {
      isMounted = false;
    };
  }, [tourDays]);

  if (isLoading) {
    return <Text style={styles.routeInfoText}>Calculating route information...</Text>;
  }

  return (
    <>
      <Text style={styles.routeInfoText}>
        Total Distance: {routeInfo.totalDistance} km ({Math.round(routeInfo.totalDistance * 0.621371)} miles)
      </Text>
      <Text style={styles.routeInfoText}>
        Driving Time: {Math.floor(routeInfo.totalDuration / 60)}h {Math.round(routeInfo.totalDuration % 60)}m
      </Text>
      <Text style={styles.routeInfoText}>
        Estimated Fuel: {routeInfo.totalFuelUsed} gallons (${routeInfo.totalFuelCost.toFixed(2)})
      </Text>
    </>
  );
});

export default TourPlanner; 